import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { Bed, Star, Trash2, Plus, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Unsaved Changes Modal Component
const UnsavedChangesModal = ({ isOpen, onSave, onDiscard, onCancel }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-bold text-[var(--color-text-primary)] mb-2">Nespremljene promjene</h3>
        <p className="text-gray-600 mb-6">Imate nespremljene promjene. Želite li ih spremiti prije nego nastavite?</p>
        <div className="flex gap-3 justify-end">
          <Button 
            variant="outline" 
            onClick={onCancel}
            className="border-gray-300"
          >
            Odustani
          </Button>
          <Button 
            variant="outline" 
            onClick={onDiscard}
            className="border-red-300 text-red-600 bg-[var(--color-background-light)]:bg-red-50"
          >
            Odbaci promjene
          </Button>
          <Button 
            onClick={onSave}
            className="bg-[var(--color-primary)] bg-[var(--color-background-light)]:bg-[var(--color-primary-bg-[var(--color-background-light)])] text-white"
          >
            Spremi
          </Button>
        </div>
      </div>
    </div>
  );
};

const RoomMediaManager = () => {
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  // {room_id: "Parcela 17"} fetched from Beds24 — used as a fallback so admin
  // sees the real Beds24 room name instead of a bare ID when no custom
  // `room_media.room_name` override is set.
  const [beds24Names, setBeds24Names] = useState({});
  const [currentLanguage, setCurrentLanguage] = useState('hr'); // Current editing language
  const [roomMedia, setRoomMedia] = useState({
    room_name: '',
    description: '',
    description_hr: '',
    description_en: '',
    description_it: '',
    description_de: '',
    images: [],
    amenities: [],
    amenities_hr: [],
    amenities_en: [],
    amenities_it: [],
    amenities_de: [],
    // New: Amenities with icons
    amenities_with_icons_hr: [],
    amenities_with_icons_en: [],
    amenities_with_icons_de: [],
    amenities_with_icons_it: [],
    // Bed/Room info with custom icons
    bed_info_hr: [],
    bed_info_en: [],
    bed_info_de: [],
    bed_info_it: [],
    bed_config: [],
    max_guests_icon_url: '',
    extra_services: [], // Keep for backward compatibility
    extra_services_hr: [],
    extra_services_en: [],
    extra_services_it: [],
    extra_services_de: [],
    custom_fields: [], // Keep for backward compatibility
    custom_fields_hr: [],
    custom_fields_en: [],
    custom_fields_it: [],
    custom_fields_de: [],
    extra_guest_price: 0 // Price per additional guest per night
  });
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newAmenity, setNewAmenity] = useState('');
  // New amenity with icon state
  const [newAmenityWithIcon, setNewAmenityWithIcon] = useState({
    name: '',
    icon_url: '',
    is_featured: false
  });
  // New bed info state
  const [newBedInfo, setNewBedInfo] = useState({
    text: '',
    icon_url: ''
  });
  const [newService, setNewService] = useState({
    name: '',
    description: '',
    type: 'checkbox', // 'checkbox' or 'quantity'
    min_quantity: 1,
    max_quantity: 10,
    max_is_nights: false,
    price_per_unit: 0
  });
  const [newCustomField, setNewCustomField] = useState({
    label: '',
    type: 'text', // 'text' or 'select'
    options: [], // For select type
    required: true
  });
  const [newOption, setNewOption] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { type: 'room' | 'language', data: ... }
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateTargetRoom, setDuplicateTargetRoom] = useState('');
  const [translating, setTranslating] = useState(false);
  const originalMediaRef = useRef(null);

  // Track changes whenever roomMedia changes
  useEffect(() => {
    if (originalMediaRef.current && selectedRoom) {
      const hasChanges = JSON.stringify(roomMedia) !== JSON.stringify(originalMediaRef.current);
      setHasUnsavedChanges(hasChanges);
    }
  }, [roomMedia, selectedRoom]);

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const token = localStorage.getItem('admin_token');

      // Fetch property configs + Beds24 room-name mapping in parallel. The
      // room names come from the cached /admin/beds24/room-names endpoint
      // (6h TTL) and let us show "Parcela 17" instead of a bare room_id.
      const [settingsRes, namesRes] = await Promise.all([
        axios.get(`${API}/admin/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios
          .get(`${API}/admin/beds24/room-names`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .catch(() => ({ data: { room_names: {} } })),
      ]);

      const propertyConfigs = settingsRes.data.property_configs || [];
      const namesMap = namesRes.data?.room_names || {};
      setBeds24Names(namesMap);
      
      // Extract all rooms from all properties
      const allRooms = [];
      propertyConfigs.forEach(prop => {
        prop.room_ids.forEach(roomId => {
          allRooms.push({
            room_id: roomId,
            property_name: prop.property_name,
            property_id: prop.property_id
          });
        });
      });
      
      // Fetch media for each room
      const roomsWithMedia = await Promise.all(
        allRooms.map(async (room) => {
          try {
            const mediaResponse = await axios.get(`${API}/admin/rooms/${room.room_id}/media`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            return {
              ...room,
              media: mediaResponse.data
            };
          } catch (error) {
            return {
              ...room,
              media: { images: [], description: null, room_name: null }
            };
          }
        })
      );
      
      setRooms(roomsWithMedia);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      toast.error('Failed to load rooms');
      setLoading(false);
    }
  };

  // Internal function to actually select the room
  const performRoomSelection = (room) => {
    const newMedia = {
      room_name: room.media?.room_name || '',
      description: room.media?.description || '',
      description_hr: room.media?.description_hr || '',
      description_en: room.media?.description_en || '',
      description_it: room.media?.description_it || '',
      description_de: room.media?.description_de || '',
      images: room.media?.images || [],
      amenities: room.media?.amenities || [],
      amenities_hr: room.media?.amenities_hr || [],
      amenities_en: room.media?.amenities_en || [],
      amenities_it: room.media?.amenities_it || [],
      amenities_de: room.media?.amenities_de || [],
      amenities_with_icons_hr: room.media?.amenities_with_icons_hr || [],
      amenities_with_icons_en: room.media?.amenities_with_icons_en || [],
      amenities_with_icons_de: room.media?.amenities_with_icons_de || [],
      amenities_with_icons_it: room.media?.amenities_with_icons_it || [],
      bed_info_hr: room.media?.bed_info_hr || [],
      bed_info_en: room.media?.bed_info_en || [],
      bed_info_de: room.media?.bed_info_de || [],
      bed_info_it: room.media?.bed_info_it || [],
      bed_config: room.media?.bed_config || [],
      extra_services: room.media?.extra_services || [],
      extra_services_hr: room.media?.extra_services_hr || [],
      extra_services_en: room.media?.extra_services_en || [],
      extra_services_it: room.media?.extra_services_it || [],
      extra_services_de: room.media?.extra_services_de || [],
      custom_fields: room.media?.custom_fields || [],
      custom_fields_hr: room.media?.custom_fields_hr || [],
      custom_fields_en: room.media?.custom_fields_en || [],
      custom_fields_it: room.media?.custom_fields_it || [],
      custom_fields_de: room.media?.custom_fields_de || [],
      extra_guest_price: room.media?.extra_guest_price || 0,
      max_guests_icon_url: room.media?.max_guests_icon_url || ''
    };
    
    setSelectedRoom(room);
    setRoomMedia(newMedia);
    originalMediaRef.current = JSON.parse(JSON.stringify(newMedia)); // Deep copy
    setHasUnsavedChanges(false);
    setNewImageUrl('');
    setNewAmenity('');
    setNewService({
      name: '',
      description: '',
      type: 'checkbox',
      min_quantity: 1,
      max_quantity: 10,
      max_is_nights: false,
      price_per_unit: 0
    });
    setNewCustomField({
      label: '',
      type: 'text',
      options: [],
      required: true
    });
    setNewOption('');
  };

  // Public function that checks for unsaved changes
  const selectRoom = (room) => {
    // Don't do anything if selecting the same room
    if (selectedRoom?.room_id === room.room_id) return;
    
    if (hasUnsavedChanges) {
      setPendingAction({ type: 'room', data: room });
      setShowUnsavedModal(true);
    } else {
      performRoomSelection(room);
    }
  };

  // Internal function to perform language change
  const performLanguageChange = (lang) => {
    setCurrentLanguage(lang);
  };

  // Public function that checks for unsaved changes before language switch
  const handleLanguageChange = (lang) => {
    if (lang === currentLanguage) return;
    
    if (hasUnsavedChanges) {
      setPendingAction({ type: 'language', data: lang });
      setShowUnsavedModal(true);
    } else {
      performLanguageChange(lang);
    }
  };

  // Handle modal actions
  const handleSaveAndContinue = async () => {
    await saveRoomMedia();
    setShowUnsavedModal(false);
    
    if (pendingAction) {
      if (pendingAction.type === 'room') {
        performRoomSelection(pendingAction.data);
      } else if (pendingAction.type === 'language') {
        performLanguageChange(pendingAction.data);
      }
      setPendingAction(null);
    }
  };

  const handleDiscardChanges = () => {
    setShowUnsavedModal(false);
    setHasUnsavedChanges(false);
    
    if (pendingAction) {
      if (pendingAction.type === 'room') {
        performRoomSelection(pendingAction.data);
      } else if (pendingAction.type === 'language') {
        performLanguageChange(pendingAction.data);
      }
      setPendingAction(null);
    }
  };

  const handleCancelAction = () => {
    setShowUnsavedModal(false);
    setPendingAction(null);
  };

  const addImageUrl = () => {
    if (!newImageUrl.trim()) {
      toast.error('Please enter an image URL');
      return;
    }
    
    if (roomMedia.images.length >= 15) {
      toast.error('Maximum 15 images allowed per room');
      return;
    }
    
    setRoomMedia(prev => ({
      ...prev,
      images: [...prev.images, newImageUrl.trim()]
    }));
    setNewImageUrl('');
  };

  const removeImage = (index) => {
    setRoomMedia(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addAmenity = () => {
    if (!newAmenity.trim()) {
      toast.error('Please enter an amenity');
      return;
    }
    
    const amenityKey = `amenities_${currentLanguage}`;
    setRoomMedia(prev => ({
      ...prev,
      [amenityKey]: [...(prev[amenityKey] || []), newAmenity.trim()]
    }));
    setNewAmenity('');
  };

  const removeAmenity = (index) => {
    const amenityKey = `amenities_${currentLanguage}`;
    setRoomMedia(prev => ({
      ...prev,
      [amenityKey]: (prev[amenityKey] || []).filter((_, i) => i !== index)
    }));
  };

  // Extra Services functions - Language specific
  const addExtraService = () => {
    if (!newService.name.trim()) {
      toast.error('Please enter service name');
      return;
    }
    if (!newService.description.trim()) {
      toast.error('Please enter service description');
      return;
    }
    if (newService.price_per_unit <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }
    
    const serviceKey = `extra_services_${currentLanguage}`;
    setRoomMedia(prev => ({
      ...prev,
      [serviceKey]: [...(prev[serviceKey] || []), { ...newService }]
    }));
    
    setNewService({
      name: '',
      description: '',
      type: 'checkbox',
      min_quantity: 1,
      max_quantity: 10,
      max_is_nights: false,
      price_per_unit: 0
    });
    toast.success('Extra service added');
  };

  const removeExtraService = (index) => {
    const serviceKey = `extra_services_${currentLanguage}`;
    setRoomMedia(prev => ({
      ...prev,
      [serviceKey]: (prev[serviceKey] || []).filter((_, i) => i !== index)
    }));
    toast.success('Extra service removed');
  };

  // Duplicate room settings to another room
  const handleDuplicateSettings = async () => {
    if (!duplicateTargetRoom) {
      toast.error('Odaberi sobu na koju želiš kopirati postavke');
      return;
    }

    const token = localStorage.getItem('admin_token');
    if (!token) {
      toast.error('Not authenticated');
      return;
    }

    try {
      // Prepare the data to copy (exclude room-specific fields and images)
      const dataToCopy = {
        ...roomMedia,
        room_name: undefined,
        images: undefined
      };
      delete dataToCopy.room_name;
      delete dataToCopy.images;

      // Save to target room using POST (same as regular save)
      await axios.post(`${API}/admin/rooms/${duplicateTargetRoom}/media`, dataToCopy, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Postavke uspješno kopirane na sobu ${duplicateTargetRoom}`);
      setShowDuplicateModal(false);
      setDuplicateTargetRoom('');
      
      // Refresh rooms to show updated data
      fetchRooms();
    } catch (error) {
      console.error('Error duplicating settings:', error);
      toast.error('Greška pri kopiranju postavki: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Copy content from current language to other languages (for manual translation)
  const handleCopyToOtherLanguages = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      toast.error('Not authenticated');
      return;
    }

    setTranslating(true);
    
    try {
      // Target languages (all except current)
      const allLangs = ['hr', 'en', 'de', 'it'];
      const targetLangs = allLangs.filter(l => l !== currentLanguage);
      
      const updatedMedia = { ...roomMedia };
      
      // Copy all content from current language to target languages
      for (const targetLang of targetLangs) {
        // Description
        if (roomMedia[`description_${currentLanguage}`]) {
          updatedMedia[`description_${targetLang}`] = roomMedia[`description_${currentLanguage}`];
        }
        
        // Amenities with icons
        const sourceAmenities = roomMedia[`amenities_with_icons_${currentLanguage}`] || [];
        if (sourceAmenities.length > 0) {
          updatedMedia[`amenities_with_icons_${targetLang}`] = sourceAmenities.map(amenity => ({
            ...amenity
          }));
        }
        
        // Bed info
        const sourceBedInfo = roomMedia[`bed_info_${currentLanguage}`] || [];
        if (sourceBedInfo.length > 0) {
          updatedMedia[`bed_info_${targetLang}`] = sourceBedInfo.map(info => ({
            ...info
          }));
        }
        
        // Extra services
        const sourceServices = roomMedia[`extra_services_${currentLanguage}`] || [];
        if (sourceServices.length > 0) {
          updatedMedia[`extra_services_${targetLang}`] = sourceServices.map(service => ({
            ...service
          }));
        }
        
        // Custom fields
        const sourceCustomFields = roomMedia[`custom_fields_${currentLanguage}`] || [];
        if (sourceCustomFields.length > 0) {
          updatedMedia[`custom_fields_${targetLang}`] = sourceCustomFields.map(field => ({
            ...field
          }));
        }
      }
      
      setRoomMedia(updatedMedia);
      toast.success(`Sadržaj kopiran na ${targetLangs.length} jezika! Sada možete ručno prevesti svaki jezik.`);
    } catch (error) {
      console.error('Copy error:', error);
      toast.error('Greška pri kopiranju: ' + error.message);
    } finally {
      setTranslating(false);
    }
  };

  // Custom Fields functions
  const addOptionToField = () => {
    if (!newOption.trim()) {
      toast.error('Please enter an option');
      return;
    }
    setNewCustomField(prev => ({
      ...prev,
      options: [...prev.options, newOption.trim()]
    }));
    setNewOption('');
  };

  const removeOptionFromField = (optionIndex) => {
    setNewCustomField(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== optionIndex)
    }));
  };

  // Custom Fields functions - Language specific
  const addCustomField = () => {
    if (!newCustomField.label.trim()) {
      toast.error('Please enter field label/question');
      return;
    }
    if (newCustomField.type === 'select' && newCustomField.options.length === 0) {
      toast.error('Please add at least one option for select field');
      return;
    }
    
    const fieldKey = `custom_fields_${currentLanguage}`;
    setRoomMedia(prev => ({
      ...prev,
      [fieldKey]: [...(prev[fieldKey] || []), { ...newCustomField }]
    }));
    
    setNewCustomField({
      label: '',
      type: 'text',
      options: [],
      required: true
    });
    setNewOption('');
    toast.success('Custom field added');
  };

  const removeCustomField = (index) => {
    const fieldKey = `custom_fields_${currentLanguage}`;
    setRoomMedia(prev => ({
      ...prev,
      [fieldKey]: (prev[fieldKey] || []).filter((_, i) => i !== index)
    }));
    toast.success('Custom field removed');
  };

  const saveRoomMedia = async () => {
    if (!selectedRoom) return;
    
    // Validate description length (max 3 sentences)
    if (roomMedia.description) {
      const sentences = roomMedia.description.split(/[.!?]+/).filter(s => s.trim().length > 0);
      if (sentences.length > 3) {
        toast.error('Description must be maximum 3 sentences');
        return;
      }
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/rooms/${selectedRoom.room_id}/media`, roomMedia, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update original state to current after successful save
      originalMediaRef.current = JSON.parse(JSON.stringify(roomMedia));
      setHasUnsavedChanges(false);
      
      toast.success('Room media saved successfully');
      fetchRooms(); // Refresh the list
    } catch (error) {
      console.error('Error saving room media:', error);
      toast.error('Failed to save room media');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-[var(--color-text-primary)] border-r-transparent"></div>
      </div>
    );
  }

  // Sort rooms by the display label (custom override → Beds24 name → ID) with
  // natural-numeric collation so "Parcela 2" < "Parcela 10". Memo so we don't
  // re-sort on every keystroke in the editor.
  const sortedRooms = (() => {
    const labelOf = r => r.media?.room_name || beds24Names[r.room_id] || `Room ${r.room_id}`;
    return [...rooms].sort((a, b) =>
      labelOf(a).localeCompare(labelOf(b), undefined, { numeric: true, sensitivity: 'base' })
    );
  })();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Room List */}
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle>Rooms</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
          {sortedRooms.map((room) => (
            <div
              key={room.room_id}
              onClick={() => selectRoom(room)}
              className={`p-3 border rounded cursor-pointer transition-colors ${
                selectedRoom?.room_id === room.room_id
                  ? 'bg-amber-50 border-amber-500'
                  : 'bg-[var(--color-background-light)]:bg-[var(--color-background-light)] border-[var(--color-border)]'
              }`}
            >
              <div className="font-medium">{room.media?.room_name || beds24Names[room.room_id] || `Room ${room.room_id}`}</div>
              <div className="text-sm text-[var(--color-text-secondary)]">{room.property_name}</div>
              <div className="text-xs text-[var(--color-text-muted)] mt-1">
                {room.media?.images?.length || 0} images
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Media Editor */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>
            {selectedRoom ? `Edit Media - ${selectedRoom.media?.room_name || beds24Names[selectedRoom.room_id] || `Room ${selectedRoom.room_id}`}` : 'Select a room to edit'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {selectedRoom ? (
            <>
              {/* Language Tabs */}
              <div className="border-b border-gray-200">
                <div className="flex gap-1">
                  {[
                    { code: 'hr', name: 'Hrvatski', flag: 'fi-hr' },
                    { code: 'en', name: 'English', flag: 'fi-gb' },
                    { code: 'it', name: 'Italiano', flag: 'fi-it' },
                    { code: 'de', name: 'Deutsch', flag: 'fi-de' }
                  ].map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                        currentLanguage === lang.code
                          ? 'border-[var(--color-primary)] text-[var(--color-primary)] font-semibold'
                          : 'border-transparent text-gray-600 bg-[var(--color-background-light)]:text-gray-900'
                      }`}
                    >
                      <span className={`fi ${lang.flag}`}></span>
                      <span className="text-sm">{lang.name}</span>
                      <span className="text-xs font-mono text-gray-500">{lang.code.toUpperCase()}</span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-2 gap-2">
                  <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded flex-1">
                    ℹ️ Editing content for <strong>{currentLanguage.toUpperCase()}</strong> language. Switch tabs to edit other languages.
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyToOtherLanguages}
                    disabled={translating}
                    className="flex items-center gap-2 text-xs"
                  >
                    {translating ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Kopiram...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Kopiraj na ostale jezike
                      </>
                    )}
                  </Button>
                  {hasUnsavedChanges && (
                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded font-medium whitespace-nowrap">
                      ⚠️ Nespremljene promjene
                    </span>
                  )}
                </div>
              </div>

              {/* Room Name */}
              <div>
                <Label htmlFor="room-name">Room Name</Label>
                <Input
                  id="room-name"
                  value={roomMedia.room_name}
                  onChange={(e) => setRoomMedia(prev => ({ ...prev, room_name: e.target.value }))}
                  placeholder="e.g., Deluxe Ocean View Suite"
                />
              </div>

              {/* Extra Guest Price */}
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <Label htmlFor="extra-guest-price" className="text-base font-semibold text-[var(--color-text-primary)]">
                  Cijena dodatnog gosta (€ po noći)
                </Label>
                <p className="text-sm text-gray-600 mb-3">
                  Osnovna cijena uključuje 1 gosta. Svaki dodatni gost se naplaćuje ovaj iznos po noćenju.
                </p>
                <div className="flex items-center gap-3">
                  <Input
                    id="extra-guest-price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={roomMedia.extra_guest_price || 0}
                    onChange={(e) => setRoomMedia(prev => ({ ...prev, extra_guest_price: parseFloat(e.target.value) || 0 }))}
                    placeholder="0.00"
                    className="w-32"
                  />
                  <span className="text-sm text-gray-600">€ / dodatni gost / noć</span>
                </div>
                <p className="text-xs text-amber-700 mt-2">
                  Primjer: Za baznu cijenu €75/noć i €15 po dodatnom gostu → 2 gosta = €90/noć, 3 gosta = €105/noć
                </p>
              </div>

              {/* Max Guests Icon */}
              <div className="bg-[var(--color-background-light)] p-4 rounded-lg border border-[var(--color-border)]">
                <Label className="text-base font-semibold text-[var(--color-text-primary)]">
                  Ikona za "Maks. Gosti"
                </Label>
                <p className="text-sm text-gray-600 mb-3">
                  Dodaj svoju ikonu koja će se prikazivati pored broja gostiju na kartici sobe.
                </p>
                <div className="flex items-center gap-3">
                  <Input
                    value={roomMedia.max_guests_icon_url || ''}
                    onChange={(e) => setRoomMedia(prev => ({ ...prev, max_guests_icon_url: e.target.value }))}
                    placeholder="https://example.com/guests-icon.svg"
                    className="flex-1"
                  />
                  {roomMedia.max_guests_icon_url && (
                    <div className="w-10 h-10 border rounded flex items-center justify-center bg-white">
                      <img 
                        src={roomMedia.max_guests_icon_url} 
                        alt="Preview" 
                        className="w-6 h-6 object-contain"
                        style={{ filter: 'sepia(1) saturate(5) hue-rotate(5deg) brightness(0.9)' }}
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Description (Max 3 sentences) - {currentLanguage.toUpperCase()}</Label>
                <Textarea
                  id="description"
                  value={roomMedia[`description_${currentLanguage}`] || ''}
                  onChange={(e) => setRoomMedia(prev => ({ ...prev, [`description_${currentLanguage}`]: e.target.value }))}
                  placeholder={`Brief description of the room in ${currentLanguage.toUpperCase()}...`}
                  rows={3}
                />
              </div>

              {/* Pogodnosti s Ikonama - Featured amenities shown on property cards */}
              <div className="border-t pt-6 mt-6">
                <Label className="text-lg font-bold text-[var(--color-text-primary)] flex items-center gap-2">
                  <Star className="w-5 h-5" />
                  Pogodnosti s Ikonama - {currentLanguage.toUpperCase()}
                </Label>
                <p className="text-sm text-gray-500 mb-2">Dodaj pogodnosti s custom ikonama. Označi do 4 kao "Featured" za prikaz na kartici sobe.</p>
                
                {/* Add Amenity with Icon Form */}
                <div className="bg-amber-50 p-4 rounded-lg mb-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Naziv pogodnosti ({currentLanguage.toUpperCase()}) *</Label>
                      <Input
                        value={newAmenityWithIcon.name}
                        onChange={(e) => setNewAmenityWithIcon({...newAmenityWithIcon, name: e.target.value})}
                        placeholder="npr. Klima uređaj"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">URL ikone (opcionalno)</Label>
                      <Input
                        value={newAmenityWithIcon.icon_url}
                        onChange={(e) => setNewAmenityWithIcon({...newAmenityWithIcon, icon_url: e.target.value})}
                        placeholder="https://example.com/icon.svg"
                        className="text-sm"
                      />
                    </div>
                    <div className="flex items-end gap-2">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newAmenityWithIcon.is_featured}
                          onChange={(e) => setNewAmenityWithIcon({...newAmenityWithIcon, is_featured: e.target.checked})}
                          className="w-4 h-4"
                        />
                        Featured (prikaži na kartici)
                      </label>
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      if (!newAmenityWithIcon.name.trim()) {
                        toast.error('Unesite naziv pogodnosti');
                        return;
                      }
                      const key = `amenities_with_icons_${currentLanguage}`;
                      const currentAmenities = roomMedia[key] || [];
                      const featuredCount = currentAmenities.filter(a => a.is_featured).length;
                      if (newAmenityWithIcon.is_featured && featuredCount >= 4) {
                        toast.error('Maksimalno 4 featured pogodnosti');
                        return;
                      }
                      setRoomMedia({
                        ...roomMedia,
                        [key]: [...currentAmenities, {...newAmenityWithIcon}]
                      });
                      setNewAmenityWithIcon({ name: '', icon_url: '', is_featured: false });
                    }}
                    size="sm"
                    className="bg-amber-600 bg-[var(--color-background-light)]:bg-amber-700"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Dodaj pogodnost
                  </Button>
                </div>

                {/* Amenities with Icons List - Enhanced with Edit, Reorder, Toggle Featured */}
                <div className="space-y-2 mb-4">
                  {(roomMedia[`amenities_with_icons_${currentLanguage}`] || []).map((amenity, index) => {
                    const key = `amenities_with_icons_${currentLanguage}`;
                    const amenities = roomMedia[key] || [];
                    const featuredCount = amenities.filter(a => a.is_featured).length;
                    
                    return (
                      <div 
                        key={index}
                        className={`flex items-center gap-2 p-3 rounded-lg border ${amenity.is_featured ? 'bg-amber-50 border-amber-300' : 'bg-[var(--color-background-light)] border-[var(--color-border)]'}`}
                      >
                        {/* Reorder Buttons */}
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => {
                              if (index === 0) return;
                              const newAmenities = [...amenities];
                              [newAmenities[index - 1], newAmenities[index]] = [newAmenities[index], newAmenities[index - 1]];
                              setRoomMedia({ ...roomMedia, [key]: newAmenities });
                            }}
                            className={`p-1 rounded ${index === 0 ? 'text-[var(--color-border)] cursor-not-allowed' : 'text-[var(--color-text-muted)] bg-[var(--color-background-light)]:bg-[var(--color-border-light)]'}`}
                            disabled={index === 0}
                            title="Pomakni gore"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              if (index === amenities.length - 1) return;
                              const newAmenities = [...amenities];
                              [newAmenities[index], newAmenities[index + 1]] = [newAmenities[index + 1], newAmenities[index]];
                              setRoomMedia({ ...roomMedia, [key]: newAmenities });
                            }}
                            className={`p-1 rounded ${index === amenities.length - 1 ? 'text-[var(--color-border)] cursor-not-allowed' : 'text-[var(--color-text-muted)] bg-[var(--color-background-light)]:bg-[var(--color-border-light)]'}`}
                            disabled={index === amenities.length - 1}
                            title="Pomakni dolje"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>
                        
                        {/* Icon */}
                        {amenity.icon_url ? (
                          <img src={amenity.icon_url} alt="" className="w-6 h-6 object-contain flex-shrink-0" onError={(e) => e.target.style.display = 'none'} />
                        ) : (
                          <div className="w-6 h-6 bg-[var(--color-border)] rounded flex-shrink-0" />
                        )}
                        
                        {/* Editable Name */}
                        <Input
                          value={amenity.name}
                          onChange={(e) => {
                            const newAmenities = [...amenities];
                            newAmenities[index] = { ...amenity, name: e.target.value };
                            setRoomMedia({ ...roomMedia, [key]: newAmenities });
                          }}
                          className="flex-1 text-sm h-8"
                          placeholder="Naziv pogodnosti"
                        />
                        
                        {/* Editable Icon URL */}
                        <Input
                          value={amenity.icon_url || ''}
                          onChange={(e) => {
                            const newAmenities = [...amenities];
                            newAmenities[index] = { ...amenity, icon_url: e.target.value };
                            setRoomMedia({ ...roomMedia, [key]: newAmenities });
                          }}
                          className="w-40 text-xs h-8 hidden md:block"
                          placeholder="URL ikone"
                        />
                        
                        {/* Featured Toggle */}
                        <button
                          onClick={() => {
                            if (!amenity.is_featured && featuredCount >= 4) {
                              toast.error('Maksimalno 4 featured pogodnosti');
                              return;
                            }
                            const newAmenities = [...amenities];
                            newAmenities[index] = { ...amenity, is_featured: !amenity.is_featured };
                            setRoomMedia({ ...roomMedia, [key]: newAmenities });
                          }}
                          className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                            amenity.is_featured 
                              ? 'bg-amber-500 text-white bg-[var(--color-background-light)]:bg-amber-600' 
                              : 'bg-[var(--color-border-light)] text-[var(--color-text-secondary)] bg-[var(--color-background-light)]:bg-amber-100 bg-[var(--color-background-light)]:text-amber-700'
                          }`}
                          title={amenity.is_featured ? 'Ukloni iz Featured' : 'Dodaj u Featured'}
                        >
                          {amenity.is_featured ? '★ Featured' : '☆ Featured'}
                        </button>
                        
                        {/* Delete Button */}
                        <button
                          onClick={() => {
                            setRoomMedia({
                              ...roomMedia,
                              [key]: amenities.filter((_, i) => i !== index)
                            });
                          }}
                          className="text-red-500 bg-[var(--color-background-light)]:text-red-700 p-1"
                          title="Obriši pogodnost"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                  {(roomMedia[`amenities_with_icons_${currentLanguage}`] || []).length === 0 && (
                    <p className="text-sm text-[var(--color-text-muted)] italic">Nema dodanih pogodnosti s ikonama za {currentLanguage.toUpperCase()}</p>
                  )}
                </div>
                
                {/* Featured Count Info */}
                {(roomMedia[`amenities_with_icons_${currentLanguage}`] || []).length > 0 && (
                  <div className="text-xs text-[var(--color-text-muted)] mb-4">
                    Featured: {(roomMedia[`amenities_with_icons_${currentLanguage}`] || []).filter(a => a.is_featured).length}/4 
                    | Ukupno: {(roomMedia[`amenities_with_icons_${currentLanguage}`] || []).length} pogodnosti
                  </div>
                )}
              </div>

              {/* Bed/Room Info - Custom icons and text */}
              <div className="border-t pt-6 mt-6">
                <Label className="text-lg font-bold text-[var(--color-text-primary)] flex items-center gap-2">
                  <Bed className="w-5 h-5" />
                  Info Sobe (Ležaji, Kapacitet) - {currentLanguage.toUpperCase()}
                </Label>
                <p className="text-sm text-gray-500 mb-2">Dodaj informacije o ležajima i sobi s custom ikonama. Prikazuju se pored "Maks. Gosti" na kartici.</p>
                
                {/* Add Bed Info Form */}
                <div className="bg-blue-50 p-4 rounded-lg mb-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Tekst ({currentLanguage.toUpperCase()}) *</Label>
                      <Input
                        value={newBedInfo?.text || ''}
                        onChange={(e) => setNewBedInfo({...newBedInfo, text: e.target.value})}
                        placeholder="npr. Bračni / Single, Sofa krevet, 25 m²"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">URL ikone</Label>
                      <Input
                        value={newBedInfo?.icon_url || ''}
                        onChange={(e) => setNewBedInfo({...newBedInfo, icon_url: e.target.value})}
                        placeholder="https://example.com/bed-icon.svg"
                        className="text-sm"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      if (!newBedInfo?.text?.trim()) {
                        toast.error('Unesite tekst');
                        return;
                      }
                      const key = `bed_info_${currentLanguage}`;
                      const currentBedInfo = roomMedia[key] || [];
                      setRoomMedia({
                        ...roomMedia,
                        [key]: [...currentBedInfo, {...newBedInfo}]
                      });
                      setNewBedInfo({ text: '', icon_url: '' });
                    }}
                    size="sm"
                    className="bg-blue-600 bg-[var(--color-background-light)]:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Dodaj info
                  </Button>
                </div>

                {/* Bed Info List - Enhanced with Edit, Reorder */}
                <div className="space-y-2 mb-4">
                  {(roomMedia[`bed_info_${currentLanguage}`] || []).map((info, index) => {
                    const key = `bed_info_${currentLanguage}`;
                    const bedInfoList = roomMedia[key] || [];
                    
                    return (
                      <div 
                        key={index}
                        className="flex items-center gap-2 p-3 rounded-lg border bg-[var(--color-background-light)] border-[var(--color-border)]"
                      >
                        {/* Reorder Buttons */}
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => {
                              if (index === 0) return;
                              const newList = [...bedInfoList];
                              [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
                              setRoomMedia({ ...roomMedia, [key]: newList });
                            }}
                            className={`p-1 rounded ${index === 0 ? 'text-[var(--color-border)] cursor-not-allowed' : 'text-[var(--color-text-muted)] bg-[var(--color-background-light)]:bg-[var(--color-border-light)]'}`}
                            disabled={index === 0}
                            title="Pomakni gore"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              if (index === bedInfoList.length - 1) return;
                              const newList = [...bedInfoList];
                              [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
                              setRoomMedia({ ...roomMedia, [key]: newList });
                            }}
                            className={`p-1 rounded ${index === bedInfoList.length - 1 ? 'text-[var(--color-border)] cursor-not-allowed' : 'text-[var(--color-text-muted)] bg-[var(--color-background-light)]:bg-[var(--color-border-light)]'}`}
                            disabled={index === bedInfoList.length - 1}
                            title="Pomakni dolje"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>
                        
                        {/* Icon */}
                        {info.icon_url ? (
                          <img src={info.icon_url} alt="" className="w-6 h-6 object-contain flex-shrink-0" onError={(e) => e.target.style.display = 'none'} />
                        ) : (
                          <Bed className="w-6 h-6 text-[var(--color-text-muted)] flex-shrink-0" />
                        )}
                        
                        {/* Editable Text */}
                        <Input
                          value={info.text}
                          onChange={(e) => {
                            const newList = [...bedInfoList];
                            newList[index] = { ...info, text: e.target.value };
                            setRoomMedia({ ...roomMedia, [key]: newList });
                          }}
                          className="flex-1 text-sm h-8"
                          placeholder="Naziv (npr. Bračni krevet)"
                        />
                        
                        {/* Editable Icon URL */}
                        <Input
                          value={info.icon_url || ''}
                          onChange={(e) => {
                            const newList = [...bedInfoList];
                            newList[index] = { ...info, icon_url: e.target.value };
                            setRoomMedia({ ...roomMedia, [key]: newList });
                          }}
                          className="w-40 text-xs h-8 hidden md:block"
                          placeholder="URL ikone"
                        />
                        
                        {/* Delete Button */}
                        <button
                          onClick={() => {
                            setRoomMedia({
                              ...roomMedia,
                              [key]: bedInfoList.filter((_, i) => i !== index)
                            });
                          }}
                          className="text-red-500 bg-[var(--color-background-light)]:text-red-700 p-1"
                          title="Obriši"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                  {(roomMedia[`bed_info_${currentLanguage}`] || []).length === 0 && (
                    <p className="text-sm text-[var(--color-text-muted)] italic">Nema dodanih informacija o ležajima za {currentLanguage.toUpperCase()}</p>
                  )}
                </div>
              </div>

              {/* Extra Services */}
              <div className="border-t pt-6 mt-6">
                <Label className="text-lg font-bold text-[var(--color-text-primary)]">Extra Services - {currentLanguage.toUpperCase()}</Label>
                <p className="text-sm text-gray-500 mb-2">Add paid extra services that guests can select during booking</p>
                <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded mb-4">
                  ℹ️ Enter service names and descriptions in <strong>{currentLanguage.toUpperCase()}</strong>. Switch language tabs to add translations.
                </div>
                
                {/* Add Extra Service Form */}
                <div className="bg-[var(--color-background-light)] p-4 rounded-lg mb-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Service Name ({currentLanguage.toUpperCase()}) *</Label>
                      <Input
                        value={newService.name}
                        onChange={(e) => setNewService({...newService, name: e.target.value})}
                        placeholder={`e.g., Airport Transfer (in ${currentLanguage.toUpperCase()})`}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Short Description ({currentLanguage.toUpperCase()}) *</Label>
                      <Input
                        value={newService.description}
                        onChange={(e) => setNewService({...newService, description: e.target.value})}
                        placeholder={`e.g., To/from airport (in ${currentLanguage.toUpperCase()})`}
                        className="text-sm"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Type *</Label>
                      <select
                        value={newService.type}
                        onChange={(e) => setNewService({...newService, type: e.target.value})}
                        className="w-full border rounded-md px-3 py-2 text-sm"
                      >
                        <option value="checkbox">Checkbox (Yes/No)</option>
                        <option value="quantity">Quantity</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Price (€) - One-time *</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newService.price_per_unit}
                        onChange={(e) => setNewService({...newService, price_per_unit: parseFloat(e.target.value) || 0})}
                        placeholder="0.00"
                        className="text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">Applied once per stay, not per night</p>
                    </div>
                    {newService.type === 'quantity' && (
                      <div>
                        <Label className="text-xs">Min Quantity</Label>
                        <Input
                          type="number"
                          min="1"
                          value={newService.min_quantity}
                          onChange={(e) => setNewService({...newService, min_quantity: parseInt(e.target.value) || 1})}
                          className="text-sm"
                        />
                      </div>
                    )}
                  </div>
                  
                  {newService.type === 'quantity' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={newService.max_is_nights}
                            onChange={(e) => setNewService({...newService, max_is_nights: e.target.checked})}
                          />
                          Max = Number of Nights
                        </Label>
                        <p className="text-xs text-gray-500 mt-1">Check this to set max quantity to guest&apos;s number of nights</p>
                      </div>
                      {!newService.max_is_nights && (
                        <div>
                          <Label className="text-xs">Max Quantity</Label>
                          <Input
                            type="number"
                            min={newService.min_quantity}
                            value={newService.max_quantity}
                            onChange={(e) => setNewService({...newService, max_quantity: parseInt(e.target.value) || 10})}
                            className="text-sm"
                          />
                        </div>
                      )}
                    </div>
                  )}
                  
                  <Button onClick={addExtraService} className="w-full bg-[var(--color-primary)] bg-[var(--color-background-light)]:bg-[var(--color-primary-bg-[var(--color-background-light)])]">
                    Add Extra Service
                  </Button>
                </div>

                {/* Extra Services List - Language Specific */}
                <div className="space-y-2">
                  {(roomMedia[`extra_services_${currentLanguage}`] || []).map((service, index) => (
                    <div 
                      key={index}
                      className="flex items-start justify-between p-3 bg-white border rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm">{service.name}</span>
                          <span className="text-xs px-2 py-0.5 bg-[var(--color-border-light)] rounded-full">
                            {service.type === 'checkbox' ? 'Yes/No' : 'Quantity'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mb-1">{service.description}</p>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="font-semibold text-[var(--color-primary)]">€{service.price_per_unit.toFixed(2)} (one-time)</span>
                          {service.type === 'quantity' && (
                            <span>
                              Qty: {service.min_quantity}-{service.max_is_nights ? 'nights' : service.max_quantity}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => removeExtraService(index)}
                        className="text-red-600 bg-[var(--color-background-light)]:text-red-800 font-bold text-lg px-2"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {(roomMedia[`extra_services_${currentLanguage}`] || []).length === 0 && (
                    <p className="text-sm text-[var(--color-text-muted)] text-center py-4">No extra services added for {currentLanguage.toUpperCase()} yet</p>
                  )}
                </div>
              </div>

              {/* Custom Form Fields */}
              <div className="border-t pt-6 mt-6">
                <Label className="text-lg font-bold text-[var(--color-text-primary)]">Custom Guest Information Fields - {currentLanguage.toUpperCase()}</Label>
                <p className="text-sm text-gray-500 mb-2">Add custom fields to guest information form (answers sent to Beds24 notes)</p>
                <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded mb-4">
                  ℹ️ Enter field labels in <strong>{currentLanguage.toUpperCase()}</strong>. Switch language tabs to add translations.
                </div>
                
                {/* Add Custom Field Form */}
                <div className="bg-[var(--color-background-light)] p-4 rounded-lg mb-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Question/Label ({currentLanguage.toUpperCase()}) *</Label>
                      <Input
                        value={newCustomField.label}
                        onChange={(e) => setNewCustomField({...newCustomField, label: e.target.value})}
                        placeholder={`e.g., Beds connected or separated? (in ${currentLanguage.toUpperCase()})`}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Field Type *</Label>
                      <select
                        value={newCustomField.type}
                        onChange={(e) => setNewCustomField({...newCustomField, type: e.target.value, options: e.target.value === 'text' ? [] : newCustomField.options})}
                        className="w-full border rounded-md px-3 py-2 text-sm"
                      >
                        <option value="text">Text Input</option>
                        <option value="select">Dropdown/Select</option>
                      </select>
                    </div>
                  </div>
                  
                  {newCustomField.type === 'select' && (
                    <div>
                      <Label className="text-xs">Dropdown Options</Label>
                      <div className="flex gap-2 mb-2">
                        <Input
                          value={newOption}
                          onChange={(e) => setNewOption(e.target.value)}
                          placeholder="Enter an option"
                          className="text-sm"
                          onKeyPress={(e) => e.key === 'Enter' && addOptionToField()}
                        />
                        <Button 
                          type="button"
                          onClick={addOptionToField}
                          className="bg-[var(--color-dark)] bg-[var(--color-background-light)]:bg-[var(--color-dark-bg-[var(--color-background-light)])] whitespace-nowrap"
                        >
                          Add Option
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {newCustomField.options.map((option, idx) => (
                          <span 
                            key={idx}
                            className="inline-flex items-center gap-1 bg-white border px-2 py-1 rounded text-sm"
                          >
                            {option}
                            <button
                              onClick={() => removeOptionFromField(idx)}
                              className="text-red-600 bg-[var(--color-background-light)]:text-red-800 font-bold ml-1"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newCustomField.required}
                      onChange={(e) => setNewCustomField({...newCustomField, required: e.target.checked})}
                      id="field-required"
                    />
                    <Label htmlFor="field-required" className="text-xs cursor-pointer">
                      Required field
                    </Label>
                  </div>
                  
                  <Button onClick={addCustomField} className="w-full bg-[var(--color-primary)] bg-[var(--color-background-light)]:bg-[var(--color-primary-bg-[var(--color-background-light)])]">
                    Add Custom Field
                  </Button>
                </div>

                {/* Custom Fields List - Language Specific */}
                <div className="space-y-2">
                  {(roomMedia[`custom_fields_${currentLanguage}`] || []).map((field, index) => (
                    <div 
                      key={index}
                      className="flex items-start justify-between p-3 bg-white border rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm">{field.label}</span>
                          <span className="text-xs px-2 py-0.5 bg-[var(--color-border-light)] rounded-full">
                            {field.type === 'text' ? 'Text' : 'Dropdown'}
                          </span>
                          {field.required && (
                            <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                              Required
                            </span>
                          )}
                        </div>
                        {field.type === 'select' && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {field.options.map((opt, idx) => (
                              <span key={idx} className="text-xs bg-[var(--color-background)] px-2 py-1 rounded">
                                {opt}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => removeCustomField(index)}
                        className="text-red-600 bg-[var(--color-background-light)]:text-red-800 font-bold text-lg px-2"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {(roomMedia[`custom_fields_${currentLanguage}`] || []).length === 0 && (
                    <p className="text-sm text-[var(--color-text-muted)] text-center py-4">No custom fields added for {currentLanguage.toUpperCase()} yet</p>
                  )}
                </div>
              </div>

              {/* Images */}
              <div className="border-t pt-6 mt-6">
                <Label>Images ({roomMedia.images.length}/15)</Label>
                
                {/* Add Image URL */}
                <div className="flex gap-2 mt-2 mb-4">
                  <Input
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    placeholder="Enter image URL"
                    onKeyPress={(e) => e.key === 'Enter' && addImageUrl()}
                  />
                  <Button onClick={addImageUrl} disabled={roomMedia.images.length >= 15}>
                    Add URL
                  </Button>
                </div>

                {/* Image List */}
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {roomMedia.images.map((imageUrl, index) => (
                    <div key={index} className="flex items-center gap-3 p-2 border rounded">
                      <img 
                        src={imageUrl} 
                        alt={`Room ${index + 1}`}
                        className="w-16 h-16 object-cover rounded"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64"><rect width="64" height="64" fill="%23ddd"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23999">No Image</text></svg>';
                        }}
                      />
                      <div className="flex-1 text-sm text-[var(--color-text-secondary)] truncate">{imageUrl}</div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeImage(index)}
                        className="text-red-600 bg-[var(--color-background-light)]:text-red-700"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  {roomMedia.images.length === 0 && (
                    <p className="text-sm text-[var(--color-text-muted)] text-center py-8">
                      No images added yet. Add image URLs above.
                    </p>
                  )}
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-between items-center pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowDuplicateModal(true)}
                  className="flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Kopiraj na drugu sobu
                </Button>
                <Button
                  onClick={saveRoomMedia}
                  disabled={saving}
                  className="bg-[var(--color-primary-dark)]"
                >
                  {saving ? 'Saving...' : 'Save Room Media'}
                </Button>
              </div>
            </>
          ) : (
            <p className="text-center text-[var(--color-text-muted)] py-12">
              Select a room from the list to manage its images and description
            </p>
          )}
        </CardContent>
      </Card>
      
      {/* Duplicate Settings Modal */}
      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4">Kopiraj postavke na drugu sobu</h3>
            <p className="text-sm text-gray-600 mb-4">
              Sve postavke trenutne sobe (pogodnosti, ležaji, opisi, cijene...) bit će kopirane na odabranu sobu. Slike se NE kopiraju.
            </p>
            
            <div className="mb-4">
              <Label className="mb-2 block">Odaberi ciljnu sobu:</Label>
              <select
                value={duplicateTargetRoom}
                onChange={(e) => setDuplicateTargetRoom(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-2"
              >
                <option value="">-- Odaberi sobu --</option>
                {sortedRooms
                  .filter(r => r.room_id !== selectedRoom?.room_id)
                  .map(room => (
                    <option key={room.room_id} value={room.room_id}>
                      {room.media?.room_name || beds24Names[room.room_id] || `Room ${room.room_id}`} ({room.property_name})
                    </option>
                  ))
                }
              </select>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicateTargetRoom('');
                }}
              >
                Odustani
              </Button>
              <Button
                onClick={handleDuplicateSettings}
                disabled={!duplicateTargetRoom}
                className="bg-[var(--color-primary)]"
              >
                Kopiraj postavke
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onSave={handleSaveAndContinue}
        onDiscard={handleDiscardChanges}
        onCancel={handleCancelAction}
      />
    </div>
  );
};

export default RoomMediaManager;
